/**
 * Sign-in (spec §2.7): Sign in with Apple, Sign in with Google, email magic
 * link — all through Firebase Auth. The app only ever forwards the Firebase
 * ID token to the API; no email reaches the application database.
 *
 * `dev` mode (local builds, tests) mints HS256 tokens from the API instead.
 */
import { AUTH_MODE, FIREBASE_CONFIG } from '../config.ts';
import { api } from './api.ts';
import { isNative } from './native.ts';

export interface AuthProvider {
  readonly mode: 'dev' | 'firebase';
  init(): Promise<void>;
  getToken(): Promise<string | null>;
  isSignedIn(): boolean;
  signInWithGoogle(): Promise<void>;
  signInWithApple(): Promise<void>;
  sendMagicLink(email: string): Promise<void>;
  completeMagicLink(url: string): Promise<boolean>;
  signOut(): Promise<void>;
  deleteAuthAccount(): Promise<void>;
  onChange(cb: () => void): () => void;
}

const DEV_UID_KEY = 'drawearth.dev.uid';
const DEV_SIGNED_IN_KEY = 'drawearth.dev.signedIn';

/**
 * crypto.randomUUID only exists in a secure context, so it is missing when the
 * app is served over plain HTTP — which is how the live-reload build loads from
 * the dev server. crypto.getRandomValues has no such restriction, so fall back
 * to building a version 4 UUID from it.
 */
export function randomUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const hex = [...b].map((n) => n.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

class DevAuth implements AuthProvider {
  readonly mode = 'dev' as const;
  private token: { value: string; expiresAt: number } | null = null;
  private listeners = new Set<() => void>();
  private signedIn = false;

  async init(): Promise<void> {
    this.signedIn = localStorage.getItem(DEV_SIGNED_IN_KEY) === '1';
  }

  private uid(): string {
    let uid = localStorage.getItem(DEV_UID_KEY);
    if (!uid) {
      uid = `dev-${randomUUID()}`;
      localStorage.setItem(DEV_UID_KEY, uid);
    }
    return uid;
  }

  isSignedIn(): boolean {
    return this.signedIn;
  }

  async getToken(): Promise<string | null> {
    if (!this.signedIn) return null;
    if (this.token && this.token.expiresAt > Date.now() + 60_000) return this.token.value;
    const { token } = await api.devToken(this.uid());
    this.token = { value: token, expiresAt: Date.now() + 23 * 3600_000 };
    return token;
  }

  private async signIn(): Promise<void> {
    this.signedIn = true;
    localStorage.setItem(DEV_SIGNED_IN_KEY, '1');
    this.token = null;
    await this.getToken();
    this.emit();
  }

  signInWithGoogle(): Promise<void> {
    return this.signIn();
  }

  signInWithApple(): Promise<void> {
    return this.signIn();
  }

  async sendMagicLink(): Promise<void> {
    await this.signIn();
  }

  async completeMagicLink(): Promise<boolean> {
    return false;
  }

  async signOut(): Promise<void> {
    this.signedIn = false;
    this.token = null;
    localStorage.removeItem(DEV_SIGNED_IN_KEY);
    this.emit();
  }

  async deleteAuthAccount(): Promise<void> {
    localStorage.removeItem(DEV_UID_KEY);
    await this.signOut();
  }

  onChange(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }
}

const MAGIC_EMAIL_KEY = 'drawearth.magic.email';

class FirebaseAuth implements AuthProvider {
  readonly mode = 'firebase' as const;
  private auth: import('firebase/auth').Auth | null = null;
  private listeners = new Set<() => void>();
  private signedIn = false;

  async init(): Promise<void> {
    if (!FIREBASE_CONFIG) throw new Error('Firebase config missing (VITE_FIREBASE_*)');
    const { initializeApp, getApps } = await import('firebase/app');
    const { getAuth, onAuthStateChanged, indexedDBLocalPersistence, initializeAuth, browserPopupRedirectResolver } = await import('firebase/auth');
    const app = getApps()[0] ?? initializeApp(FIREBASE_CONFIG);
    // On native, Firebase JS must use IndexedDB persistence (no browser popup resolver).
    this.auth = isNative() ? initializeAuth(app, { persistence: indexedDBLocalPersistence }) : getAuth(app);
    void browserPopupRedirectResolver;
    await new Promise<void>((resolve) => {
      onAuthStateChanged(this.auth!, (u) => {
        this.signedIn = !!u;
        this.emit();
        resolve();
      });
    });
  }

  isSignedIn(): boolean {
    return this.signedIn;
  }

  async getToken(): Promise<string | null> {
    const u = this.auth?.currentUser;
    if (!u) return null;
    return u.getIdToken();
  }

  async signInWithGoogle(): Promise<void> {
    const { GoogleAuthProvider, signInWithPopup, signInWithCredential } = await import('firebase/auth');
    if (isNative()) {
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      const result = await FirebaseAuthentication.signInWithGoogle();
      const cred = GoogleAuthProvider.credential(result.credential?.idToken);
      await signInWithCredential(this.auth!, cred);
    } else {
      await signInWithPopup(this.auth!, new GoogleAuthProvider());
    }
  }

  async signInWithApple(): Promise<void> {
    const { OAuthProvider, signInWithPopup, signInWithCredential } = await import('firebase/auth');
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    if (isNative()) {
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      const result = await FirebaseAuthentication.signInWithApple({ skipNativeAuth: true });
      const cred = provider.credential({ idToken: result.credential?.idToken, rawNonce: result.credential?.nonce });
      await signInWithCredential(this.auth!, cred);
    } else {
      await signInWithPopup(this.auth!, provider);
    }
  }

  async sendMagicLink(email: string): Promise<void> {
    const { sendSignInLinkToEmail } = await import('firebase/auth');
    await sendSignInLinkToEmail(this.auth!, email, { url: `${location.origin}/signin`, handleCodeInApp: true });
    localStorage.setItem(MAGIC_EMAIL_KEY, email);
  }

  async completeMagicLink(url: string): Promise<boolean> {
    const { isSignInWithEmailLink, signInWithEmailLink } = await import('firebase/auth');
    if (!isSignInWithEmailLink(this.auth!, url)) return false;
    const email = localStorage.getItem(MAGIC_EMAIL_KEY) ?? window.prompt('Confirm your email to finish signing in') ?? '';
    await signInWithEmailLink(this.auth!, email, url);
    localStorage.removeItem(MAGIC_EMAIL_KEY);
    return true;
  }

  async signOut(): Promise<void> {
    await this.auth?.signOut();
    if (isNative()) {
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      await FirebaseAuthentication.signOut();
    }
  }

  async deleteAuthAccount(): Promise<void> {
    const { deleteUser } = await import('firebase/auth');
    if (this.auth?.currentUser) await deleteUser(this.auth.currentUser);
  }

  onChange(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }
}

export const auth: AuthProvider = AUTH_MODE === 'firebase' && FIREBASE_CONFIG ? new FirebaseAuth() : new DevAuth();
